// ── Inbox — Typing indicators + receipt broadcasts (channel) ──
//
// Holds the open client's broadcast channel, `client-msgs-${clientId}` (same
// channel the mobile app uses), through the shared channel registry
// (messages/channel-registry.js), so the corner messenger holding the same
// client shares it. Listens for client typing events and sends coach typing
// events back. Typing state is never persisted — purely transient UI signal.
//
// ⚠️ OPENING THE OPEN CONVERSATION AGAIN KEPT NOTHING LIVE. This removed the
// channel and asked for the same topic straight back, which hands back the
// channel still closing: no typing, receipts, reactions or unsends until
// another conversation was opened. A release and a new hold now keep the
// open channel (the registry's grace).
//
// The channel also carries server-emitted receipt broadcasts (`read` /
// `delivered`, from messages-list / messages-mark-delivered): when the
// CLIENT acts, the parent flips the coach's sent bubbles to Seen /
// Delivered live via the receiptHandlers passed into the factory.
// It also carries `reaction` (messages-react) and `unsent`
// (messages-unsend), which patch the open thread the same way.
//
// State owned by the factory closure:
//   - `_release` / `_heldClientId` — the hold on the active 1:1's channel
//   - `_typingSendTimer` — debounce for coach typing emits (3s)
//   - `_clientTypingTimer` — clears "client typing" UI after 3.5s
//   - `_clientTyping` — current visibility of the indicator
//
// The composer-input listener that calls `broadcastCoachTyping` is left
// in inbox.js because it needs to consult `_activeConvType` /
// `_activeClientId` — those getters could be passed in but the listener
// belongs at the parent layer where the keyboard input originates.

import { hold, send } from '../messages/channel-registry.js';

// How close to the bottom of the thread still counts as reading the newest
// message, for taking the coach to the typing bubble.
const AT_BOTTOM_PX = 24;

/**
 * Build the typing-indicator controller.
 *
 * @param {object} [receiptHandlers]
 * @param {(at?: string) => void} [receiptHandlers.onPeerRead] — client read the coach's in-app messages
 * @param {(at?: string) => void} [receiptHandlers.onPeerDelivered] — client's device received them
 * @param {(p: { messageId: string, emoji: string, op: 'added'|'removed' }) => void} [receiptHandlers.onPeerReaction] — client toggled an emoji
 * @param {(p: { messageId: string, at?: string }) => void} [receiptHandlers.onUnsent] — a message in the thread was unsent, by either side
 * @returns {{
 *   subscribeActiveClientTyping: (clientId: string | null) => void,
 *   unsubscribeActiveClientTyping: () => void,
 *   broadcastCoachTyping: () => void,
 *   renderClientTypingIndicator: () => void,
 * }}
 */
export function createInboxTyping(receiptHandlers = {}) {
  /** @type {null | (() => void)} */
  let _release = null;
  /** @type {string | null} */
  let _heldClientId = null;
  let _typingSendTimer = null;
  let _clientTypingTimer = null;
  let _clientTyping = false;

  function subscribeActiveClientTyping(clientId) {
    unsubscribeActiveClientTyping();
    if (!clientId) return;
    _heldClientId = clientId;
    _release = hold({ clientId }, {
      typing: (payload) => {
        if (payload?.who !== 'client') return;
        _clientTyping = true;
        renderClientTypingIndicator();
        if (_clientTypingTimer) clearTimeout(_clientTypingTimer);
        _clientTypingTimer = setTimeout(() => {
          _clientTyping = false;
          renderClientTypingIndicator();
        }, 3500);
      },
      // Receipt broadcasts — only the CLIENT's actions matter here (the
      // coach's own events also arrive via the server echo; ignore them).
      read: (payload) => {
        if (payload?.who !== 'client') return;
        receiptHandlers.onPeerRead?.(payload?.at);
      },
      delivered: (payload) => {
        if (payload?.who !== 'client') return;
        receiptHandlers.onPeerDelivered?.(payload?.at);
      },
      // Reactions — server-emitted from messages-react; patch the open
      // thread live instead of waiting for the 90s poll.
      reaction: (payload) => {
        if (payload?.who !== 'client' || !payload?.messageId || !payload?.emoji) return;
        receiptHandlers.onPeerReaction?.({
          messageId: payload.messageId,
          emoji: payload.emoji,
          op: payload.op === 'removed' ? 'removed' : 'added',
        });
      },
      // Unsend — server-emitted from messages-unsend. Either side's, unlike
      // the receipts above: the client unsending, or the coach unsending
      // from the phone app (the coach has two screens). The coach's own
      // unsend in this tab echoes back too; marking twice changes nothing.
      // ⚠️ Without this the coach saw a client's unsent message in full
      // until a reload (audit, 2026-10-09).
      unsent: (payload) => {
        if (!payload?.messageId) return;
        receiptHandlers.onUnsent?.({ messageId: payload.messageId, at: payload.at });
      },
    });
  }

  function unsubscribeActiveClientTyping() {
    // Let go of the hold; the registry removes the channel once no screen
    // holds it (after a grace, so a reopen keeps it live).
    if (_release) {
      _release();
      _release = null;
      _heldClientId = null;
    }
    if (_clientTypingTimer) {
      clearTimeout(_clientTypingTimer);
      _clientTypingTimer = null;
    }
    _clientTyping = false;
    renderClientTypingIndicator();
  }

  function broadcastCoachTyping() {
    if (!_heldClientId) return;
    send({ clientId: _heldClientId }, 'typing', { who: 'coach' });
  }

  // External pulse — lets OTHER channels (the group channel in
  // inbox/realtime.js) drive the same typing indicator without owning
  // this module's state.
  function pulseTypingIndicator() {
    _clientTyping = true;
    renderClientTypingIndicator();
    if (_clientTypingTimer) clearTimeout(_clientTypingTimer);
    _clientTypingTimer = setTimeout(() => {
      _clientTyping = false;
      renderClientTypingIndicator();
    }, 3500);
  }

  // Debounce wrapper for the composer-input listener. Lets the parent
  // call this on every keystroke without flooding the channel — only
  // one broadcast every 3 seconds.
  function debouncedBroadcastCoachTyping() {
    if (_typingSendTimer) return;
    broadcastCoachTyping();
    _typingSendTimer = setTimeout(() => { _typingSendTimer = null; }, 3000);
  }

  function renderClientTypingIndicator() {
    const container = document.getElementById('inbox-chat-messages');
    if (!container) return;
    let indicator = document.getElementById('inbox-typing-indicator');
    if (_clientTyping) {
      // ⚠️ ONLY A COACH ALREADY AT THE BOTTOM IS TAKEN TO THE BUBBLE. The
      // client's app sends a typing signal every few seconds while they type,
      // and each one used to scroll to the bottom, pulling a coach who was
      // reading back through earlier messages down to the newest one again
      // and again. Measured before the bubble goes in, since it adds height.
      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight <= AT_BOTTOM_PX;
      if (!indicator) {
        indicator = document.createElement('div');
        indicator.id = 'inbox-typing-indicator';
        indicator.className = 'inbox-typing-indicator';
        indicator.innerHTML = '<span class="inbox-typing-dot"></span><span class="inbox-typing-dot"></span><span class="inbox-typing-dot"></span>';
        container.appendChild(indicator);
      }
      if (atBottom) container.scrollTop = container.scrollHeight;
    } else {
      indicator?.remove();
    }
  }

  return {
    subscribeActiveClientTyping,
    unsubscribeActiveClientTyping,
    broadcastCoachTyping,
    debouncedBroadcastCoachTyping,
    renderClientTypingIndicator,
    pulseTypingIndicator,
  };
}
